import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';

interface BankAccountData {
  bankName: string;
  accountNumber: string;
  accountName: string;
  bvn: string;
}

interface BankAccountStepProps {
  initialData?: Partial<BankAccountData>;
  onNext: (data: BankAccountData) => void;
  onBack: () => void;
}

// Simplified Nigerian banks list - will be expanded later
const NIGERIAN_BANKS = [
  { name: 'Access Bank', code: '044' },
  { name: 'Citibank', code: '023' },
  { name: 'Ecobank Nigeria', code: '050' },
  { name: 'Fidelity Bank', code: '070' },
  { name: 'First Bank of Nigeria', code: '011' },
  { name: 'First City Monument Bank (FCMB)', code: '214' },
  { name: 'Globus Bank', code: '00103' },
  { name: 'Guaranty Trust Bank (GTBank)', code: '058' },
  { name: 'Heritage Bank', code: '030' },
  { name: 'Keystone Bank', code: '082' },
  { name: 'Polaris Bank', code: '076' },
  { name: 'Providus Bank', code: '101' },
  { name: 'Stanbic IBTC Bank', code: '221' },
  { name: 'Standard Chartered Bank', code: '068' },
  { name: 'Sterling Bank', code: '232' },
  { name: 'SunTrust Bank', code: '100' },
  { name: 'Union Bank of Nigeria', code: '032' },
  { name: 'United Bank for Africa (UBA)', code: '033' },
  { name: 'Unity Bank', code: '215' },
  { name: 'Wema Bank', code: '035' },
  { name: 'Zenith Bank', code: '057' },
];

export const BankAccountStep: React.FC<BankAccountStepProps> = ({
  initialData,
  onNext,
  onBack,
}) => {
  const [formData, setFormData] = useState<BankAccountData>({
    bankName: initialData?.bankName || '',
    accountNumber: initialData?.accountNumber || '',
    accountName: initialData?.accountName || '',
    bvn: initialData?.bvn || '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof BankAccountData, string>>>({});
  const [touchedFields, setTouchedFields] = useState<Set<keyof BankAccountData>>(new Set());

  // Verification states - placeholders for future NIBSS integration
  const [accountVerifying, setAccountVerifying] = useState(false);
  const [accountVerified, setAccountVerified] = useState(false);
  const [bvnVerifying, setBvnVerifying] = useState(false);
  const [bvnVerified, setBvnVerified] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Format inputs
    let formattedValue = value;
    if (name === 'accountNumber') {
      // Only allow digits, max 10 characters
      formattedValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (name === 'bvn') {
      // Only allow digits, max 11 characters
      formattedValue = value.replace(/\D/g, '').slice(0, 11);
    }

    setFormData((prev) => ({ ...prev, [name]: formattedValue }));

    // Clear error when user starts typing
    if (errors[name as keyof BankAccountData]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }

    // Reset verification states when relevant fields change
    if (name === 'accountNumber' || name === 'bankName') {
      setAccountVerified(false);
    }
    if (name === 'bvn') {
      setBvnVerified(false);
    }
  };

  const handleBlur = (field: keyof BankAccountData) => {
    setTouchedFields((prev) => new Set(prev).add(field));
    validateField(field);

    // Trigger verification on blur for specific fields
    if (field === 'accountNumber' && formData.accountNumber.length === 10 && formData.bankName) {
      handleVerifyAccount();
    } else if (field === 'bvn' && formData.bvn.length === 11) {
      handleVerifyBVN();
    }
  };

  const validateField = (field: keyof BankAccountData): string => {
    const value = formData[field];
    let error = '';

    switch (field) {
      case 'bankName':
        if (!value) error = 'Bank name is required';
        break;

      case 'accountNumber':
        if (!value) {
          error = 'Account number is required';
        } else if (!/^\d{10}$/.test(value)) {
          error = 'Account number must be exactly 10 digits';
        }
        break;

      case 'accountName':
        if (!value || value.trim().length < 3) {
          error = 'Account name is required';
        }
        break;

      case 'bvn':
        if (!value) {
          error = 'BVN is required';
        } else if (!/^\d{11}$/.test(value)) {
          error = 'BVN must be exactly 11 digits';
        }
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return error;
  };

  // Placeholder for account verification - will be replaced with actual API call
  const handleVerifyAccount = async () => {
    if (!formData.accountNumber || !formData.bankName) return;

    setAccountVerifying(true);
    setAccountVerified(false);

    try {
      // TODO: Replace with actual NIBSS account verification API call
      // For now, simulate API call with timeout
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Placeholder: Auto-fill account name (in real implementation, this comes from API)
      if (!formData.accountName) {
        setFormData((prev) => ({
          ...prev,
          accountName: 'VERIFIED ACCOUNT HOLDER NAME', // Placeholder
        }));
      }

      setAccountVerified(true);
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        accountNumber: 'Failed to verify account. Please check the account number.',
      }));
    } finally {
      setAccountVerifying(false);
    }
  };

  // Placeholder for BVN verification - will be replaced with actual NIBSS VerifyNow API
  const handleVerifyBVN = async () => {
    if (!formData.bvn) return;

    setBvnVerifying(true);
    setBvnVerified(false);

    try {
      // TODO: Replace with actual NIBSS VerifyNow API call
      // For now, simulate API call with timeout
      await new Promise((resolve) => setTimeout(resolve, 2000));

      setBvnVerified(true);
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        bvn: 'Failed to verify BVN. Please check the BVN number.',
      }));
    } finally {
      setBvnVerifying(false);
    }
  };

  const validateAll = (): boolean => {
    const fieldsToValidate: (keyof BankAccountData)[] = [
      'bankName',
      'accountNumber',
      'accountName',
      'bvn',
    ];

    const newErrors: Partial<Record<keyof BankAccountData, string>> = {};
    let hasErrors = false;

    fieldsToValidate.forEach((field) => {
      const error = validateField(field);
      if (error) {
        newErrors[field] = error;
        hasErrors = true;
      }
    });

    setErrors(newErrors);
    setTouchedFields(new Set(fieldsToValidate));

    // TODO: In production, make BVN verification mandatory
    // if (!bvnVerified) {
    //   newErrors.bvn = 'Please verify your BVN before proceeding';
    //   hasErrors = true;
    // }

    return !hasErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateAll()) {
      onNext(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="application-form">
      <div className="form-section">
        <h3>Bank Account Details</h3>
        <p className="section-description">
          Please provide your bank account information for loan disbursement. We'll verify your account details for security purposes. Fields marked with * are required.
        </p>

        {/* Bank Selection */}
        <div className="form-group">
          <label htmlFor="bankName">Bank Name *</label>
          <select
            id="bankName"
            name="bankName"
            value={formData.bankName}
            onChange={handleChange}
            onBlur={() => handleBlur('bankName')}
            required
            className={touchedFields.has('bankName') && errors.bankName ? 'error' : ''}
          >
            <option value="">Select your bank</option>
            {NIGERIAN_BANKS.map((bank) => (
              <option key={bank.code} value={bank.name}>
                {bank.name}
              </option>
            ))}
          </select>
          {touchedFields.has('bankName') && errors.bankName && (
            <span className="field-error">{errors.bankName}</span>
          )}
        </div>

        {/* Account Number with Verification */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="accountNumber">Account Number *</label>
            <div className="input-with-verification">
              <input
                type="text"
                id="accountNumber"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleChange}
                onBlur={() => handleBlur('accountNumber')}
                required
                placeholder="Enter 10-digit account number"
                maxLength={10}
                className={touchedFields.has('accountNumber') && errors.accountNumber ? 'error' : ''}
              />
              {accountVerifying && (
                <span className="verification-status verifying">
                  <Loader size={16} className="spinner" />
                  Verifying...
                </span>
              )}
              {accountVerified && (
                <span className="verification-status verified">
                  <CheckCircle size={16} />
                  Verified
                </span>
              )}
            </div>
            {touchedFields.has('accountNumber') && errors.accountNumber && (
              <span className="field-error">{errors.accountNumber}</span>
            )}
            <span className="form-hint">10-digit bank account number</span>
          </div>

          <div className="form-group">
            <label htmlFor="accountName">Account Name *</label>
            <input
              type="text"
              id="accountName"
              name="accountName"
              value={formData.accountName}
              onChange={handleChange}
              onBlur={() => handleBlur('accountName')}
              required
              placeholder="Account holder name"
              className={touchedFields.has('accountName') && errors.accountName ? 'error' : ''}
              readOnly={accountVerified}
            />
            {touchedFields.has('accountName') && errors.accountName && (
              <span className="field-error">{errors.accountName}</span>
            )}
            {accountVerified && (
              <span className="form-hint success">Auto-filled from verification</span>
            )}
          </div>
        </div>

        {/* BVN with Verification */}
        <div className="form-group">
          <label htmlFor="bvn">Bank Verification Number (BVN) *</label>
          <div className="input-with-verification">
            <input
              type="text"
              id="bvn"
              name="bvn"
              value={formData.bvn}
              onChange={handleChange}
              onBlur={() => handleBlur('bvn')}
              required
              placeholder="Enter 11-digit BVN"
              maxLength={11}
              className={touchedFields.has('bvn') && errors.bvn ? 'error' : ''}
            />
            {bvnVerifying && (
              <span className="verification-status verifying">
                <Loader size={16} className="spinner" />
                Verifying BVN...
              </span>
            )}
            {bvnVerified && (
              <span className="verification-status verified">
                <CheckCircle size={16} />
                BVN Verified
              </span>
            )}
          </div>
          {touchedFields.has('bvn') && errors.bvn && (
            <span className="field-error">{errors.bvn}</span>
          )}
          <span className="form-hint">Your BVN is required for identity verification. Dial *565*0# to get your BVN.</span>
        </div>

        {/* Info Box */}
        <div className="info-message">
          <AlertCircle size={20} />
          <div>
            <p><strong>Why do we need your BVN?</strong></p>
            <ul>
              <li>To verify your identity and prevent fraud</li>
              <li>To comply with regulatory requirements</li>
              <li>To ensure loan disbursement to the correct account</li>
            </ul>
            <p className="small-text">
              Your BVN is confidential and will be handled in accordance with data protection regulations.
              We do not have access to your account balance or transaction history.
            </p>
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="form-actions">
        <button type="button" onClick={onBack} className="btn btn-secondary">
          Back
        </button>
        <button type="submit" className="btn btn-primary">
          Continue to Loan Details
        </button>
      </div>
    </form>
  );
};
